// ── Grenade Trajectory Preview ──
// Shows a dotted arc line predicting where the grenade will land
// when a grenade is equipped. Uses a THREE.Line with dashed material.

import * as THREE from 'three';
import {
  GRENADE_GRAVITY,
  GRENADE_BOUNCE,
  GRENADE_OVERHAND_SPEED,
  GRENADE_MEDIUM_SPEED,
  GRENADE_UNDERHAND_SPEED,
} from '../core/config';
import { EFFECT_LAYER } from '../renderer/effect-loop';
import { floorYAtCached, beginFloorLookupFrame } from './floor-lookup';

const TRAJECTORY_POINTS = 60;  // number of simulated steps
const TRAJECTORY_DT = 0.03;    // time step per point (30ms)
const MAX_BOUNCES = 2;         // show up to 2 bounces in preview
const DOT_SPACING = 0.3;       // spacing between dots

const TRAJECTORY_COLOR = 0xffaa44;
const TRAJECTORY_OPACITY = 0.5;

/** Color map per grenade type for trajectory. */
const TRAJECTORY_COLORS: Record<string, number> = {
  he_grenade: 0xff4444,
  smoke_grenade: 0x999999,
  flashbang: 0xeeeeee,
  molotov: 0xff8833,
  incendiary: 0xff8833,
};

export class GrenadeTrajectory {
  private line: THREE.Line | null = null;
  private dots: THREE.Mesh[] = [];
  private scene: THREE.Scene | null = null;
  private visible = false;
  private _disposed = false;
  private dotGeo: THREE.SphereGeometry | null;
  private dotMat: THREE.MeshBasicMaterial | null;

  constructor() {
    this.dotGeo = new THREE.SphereGeometry(0.04, 4, 4);
    this.dotMat = new THREE.MeshBasicMaterial({
      color: TRAJECTORY_COLOR,
      transparent: true,
      opacity: TRAJECTORY_OPACITY,
    });
  }

  /** Show the trajectory preview. Call every frame while grenade is equipped. */
  update(
    scene: THREE.Scene,
    eyePos: THREE.Vector3,
    yaw: number,
    pitch: number,
    throwStrength: number,
    grenadeType?: string,
  ): void {
    this.scene = scene;

    // Reset the per-frame floor-Y cache once. The 60-step inner simulation
    // walks across many XZ buckets so the cache stays warm across steps and
    // multiple trajectory previews per frame all dedupe at the same buckets.
    beginFloorLookupFrame();

    // Compute throw direction from yaw + pitch (must match server: ticker_input.go)
    const dir = new THREE.Vector3(
      -Math.sin(yaw) * Math.cos(pitch),
      Math.sin(pitch),
      -Math.cos(yaw) * Math.cos(pitch),
    );

    // Server adds +0.3 upward arc for overhand/medium throws, then renormalizes
    if (throwStrength === 0 || throwStrength === 1) {
      dir.y += 0.3;
      dir.normalize();
    }

    // Get throw speed based on strength
    let speed: number;
    switch (throwStrength) {
      case 2: speed = GRENADE_UNDERHAND_SPEED; break;
      case 1: speed = GRENADE_MEDIUM_SPEED; break;
      default: speed = GRENADE_OVERHAND_SPEED; break;
    }

    const velocity = dir.clone().multiplyScalar(speed);

    // Simulate trajectory
    const points = this.simulateTrajectory(eyePos, velocity);

    // Update color based on grenade type
    const color = grenadeType ? (TRAJECTORY_COLORS[grenadeType] ?? TRAJECTORY_COLOR) : TRAJECTORY_COLOR;
    this.ensureSharedResources();
    this.dotMat!.color.setHex(color);

    // Update dots
    this.ensureDots(scene, points.length);
    for (let i = 0; i < points.length; i++) {
      const dot = this.dots[i];
      dot.position.copy(points[i]);
      dot.visible = true;
      // Fade dots toward the end
      const t = i / points.length;
      dot.scale.setScalar(1 - t * 0.6);
    }
    // Hide extra dots
    for (let i = points.length; i < this.dots.length; i++) {
      this.dots[i].visible = false;
    }

    this.visible = true;
  }

  /** Hide the trajectory preview. Dots stay in scene (invisible) and are reused on next show. */
  hide(): void {
    if (!this.visible) return;
    for (const dot of this.dots) {
      dot.visible = false;
    }
    this.visible = false;
  }

  /** Simulate the grenade arc, returning sampled positions. */
  private simulateTrajectory(startPos: THREE.Vector3, startVel: THREE.Vector3): THREE.Vector3[] {
    const points: THREE.Vector3[] = [];
    const pos = startPos.clone();
    const vel = startVel.clone();
    let bounces = 0;
    let dist = 0;

    for (let i = 0; i < TRAJECTORY_POINTS; i++) {
      // Apply gravity
      vel.y += GRENADE_GRAVITY * TRAJECTORY_DT;

      // Move
      const prevPos = pos.clone();
      pos.addScaledVector(vel, TRAJECTORY_DT);

      // Look up the floor surface immediately below the simulated point.
      // `fromY = pos.y + 0.5` keeps the ray below any catwalk/ceiling above
      // the current position so multi-floor maps (drainage at Y≈-3,
      // catwalks at Y≈3-7) resolve correctly. No BVH → legacy floorY=0.
      const groundY = floorYAtCached(pos.x, pos.z, { fromY: pos.y + 0.5 });
      const bounceY = groundY + 0.06;

      // Ground bounce (was hardcoded y ≤ 0.06; G-H-001 — use floor lookup).
      if (pos.y <= bounceY) {
        pos.y = bounceY;
        vel.y = -vel.y * GRENADE_BOUNCE;
        vel.x *= GRENADE_BOUNCE;
        vel.z *= GRENADE_BOUNCE;
        bounces++;
        if (bounces >= MAX_BOUNCES) {
          // Add final landing point
          points.push(pos.clone());
          break;
        }
      }

      // Ground friction (matches server GrenadeGroundFriction = 5.0)
      if (pos.y <= bounceY + 0.01) {
        const hSpeed = Math.sqrt(vel.x * vel.x + vel.z * vel.z);
        if (hSpeed > 0) {
          const newHSpeed = Math.max(0, hSpeed - 5.0 * TRAJECTORY_DT);
          const scale = newHSpeed / hSpeed;
          vel.x *= scale;
          vel.z *= scale;
        }
      }

      // Sample at intervals
      dist += prevPos.distanceTo(pos);
      if (dist >= DOT_SPACING) {
        points.push(pos.clone());
        dist = 0;
      }
    }

    return points;
  }

  /** Lazily recreate shared geometry/material if they were disposed. */
  private ensureSharedResources(): void {
    if (!this.dotGeo) {
      this.dotGeo = new THREE.SphereGeometry(0.04, 4, 4);
    }
    if (!this.dotMat) {
      this.dotMat = new THREE.MeshBasicMaterial({
        color: TRAJECTORY_COLOR,
        transparent: true,
        opacity: TRAJECTORY_OPACITY,
      });
    }
  }

  /** Ensure we have enough dot meshes in the scene. */
  private ensureDots(scene: THREE.Scene, needed: number): void {
    this.ensureSharedResources();
    while (this.dots.length < needed) {
      const dot = new THREE.Mesh(this.dotGeo!, this.dotMat!);
      dot.visible = false;
      dot.userData.isEffect = true;
      dot.layers.set(EFFECT_LAYER);
      scene.add(dot);
      this.dots.push(dot);
    }
  }

  /** Clean up all resources. */
  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    if (this.scene) {
      for (const dot of this.dots) {
        this.scene.remove(dot);
      }
    }
    this.dots.length = 0;
    if (this.dotGeo) {
      this.dotGeo.dispose();
      this.dotGeo = null;
    }
    if (this.dotMat) {
      this.dotMat.dispose();
      this.dotMat = null;
    }
  }
}
